import { notification } from 'antd';
import jwtDecode from 'jwt-decode';
import _ from 'lodash';
import moment from 'moment-timezone';
import { getIntl, history, setLocale } from 'umi';

import {
  accountConfirm,
  authLogin,
  authLogout,
  authPartyLogin,
  authRefreshToken,
  sendTwoFactorAuthCode,
  validateSecurityCode,
  validateSsoClient,
} from '@/services/login';
import {
  applyPostLoginRedirect,
  authRedirect,
  buildLoginUrl,
  buildPortalLoginUrl,
  consumePostLoginRedirect,
  sanitizePostLoginRedirect,
} from '@/utils/infrastructure/utilities';
import { fetchPortalEntryById } from '@/pages/CompanyPortal/portalEntry';
import { getDefaultLocale } from '@/utils/infrastructure/utilities';
import SocketService from '@/services/socket.io';
import {
  SESSION_TYPES,
  STORAGE_KEYS,
  buildRefreshContext,
  buildRefreshLoginPayload,
  resolveRefreshSession,
  clearAllUserSessions,
  clearAppLoginFlags,
  clearSsoLoginFlags,
  dropAppSessionOnLogout,
  findExchangeableAppCredential,
  getAppIdFromPath,
  getCurrentSession,
  resolvePostLoginSession,
  isActivePortalContext,
  isActiveSsoHandoff,
  isPortalPath,
  readPendingPortalLoginId,
  clearPortalLoginHandoff,
  stashPortalLoginConfig,
  shouldApplyAppLoginContext,
  findAppSessionUserId,
  setCurrentSession,
  setItem,
  getItem,
  getSessionItem,
  setSessionItem,
  removeSessionItem,
  getGlobalItem,
  setGlobalItem,
  removeGlobalItem,
  setAuthority,
  subscribeToSessionEnd,
} from '@/utils/infrastructure/auth';
import { applyPortalShellBrand, resetWhiteLabelToDefault } from '@/utils/infrastructure/whiteLabel';

const resolveAppLoginId = (payload) => {
  if (payload?._skipAppLogin) return null;
  if (payload?._appLoginAppId) return String(payload._appLoginAppId);
  if (payload?.isPortal || payload?.isAutoSignIn || payload?.headers?.portalId) return null;
  if (isPortalPath()) return null;
  const fromStore = getGlobalItem(STORAGE_KEYS.APP_LOGIN_CONTEXT);
  if (fromStore && shouldApplyAppLoginContext(fromStore)) return String(fromStore);
  if (payload?.headers?.appId && shouldApplyAppLoginContext(payload.headers.appId)) {
    return String(payload.headers.appId);
  }
  try {
    const params = new URLSearchParams(window.location.search || '');
    if (params.get('app')) return String(params.get('app'));
    // Only an explicit ?redirect=/apps/:id counts. Leftover
    // APP_REDIRECT_AFTER_LOGIN must not steal Automy OAuth on /?code=.
    const redirect = sanitizePostLoginRedirect(params.get('redirect'));
    const match = String(redirect || '').match(/\/apps\/([^/?#]+)/);
    if (match) return match[1];
  } catch (e) {}
  return null;
};

const readPortalLoginId = (payload = {}) => {
  if (payload.portalId) return String(payload.portalId);
  if (payload.headers?.portalId) return String(payload.headers.portalId);
  try {
    const query = new URLSearchParams(window.location.search || '').get('portal');
    if (query) return String(query);
  } catch (e) {}
  return readPendingPortalLoginId();
};

const withPortalLoginPayload = (payload, extra = {}) => {
  const portalId = readPortalLoginId(payload);
  if (!portalId) return extra;
  return {
    ...extra,
    isPortal: true,
    portalId,
    headers: { ...payload.headers, ...extra.headers, portalId },
  };
};

function* enterPortalAfterLogin(portalId, { select, call, put }) {
  if (!portalId) return false;

  // Do not send password/OAuth portal login through `/p/`. That gateway remints
  // with an Automy JWT; after a portal-only login there is none, and the
  // fallback dumps the tab on Automy `/`.
  let config = yield select((state) => state.companyPortal?.portal);
  if (!config?.id || String(config.id) !== String(portalId)) {
    const fetched = yield call(fetchPortalEntryById, portalId);
    config = fetched?.config || { id: portalId };
  } else if (!config.companyCode || !(config.portalCode || config.shortCode)) {
    const fetched = yield call(fetchPortalEntryById, portalId);
    if (fetched?.config) config = fetched.config;
  }

  stashPortalLoginConfig(config);
  applyPortalShellBrand(config);
  try {
    sessionStorage.removeItem('x-portal-embed-navigated');
    sessionStorage.removeItem('x-portal-template-navigated');
  } catch (e) {}
  yield put({ type: 'companyPortal/config', payload: config });
  clearPortalLoginHandoff();
  history.replace('/', config);
  yield put({ type: 'companyPortal/menu' });
  return true;
}

const twoFactor = {
  required: false,
  codeSent: false,
  providers: [],
};

const initialState = {
  status: undefined,
  accountConfirmed: false,
  twoFactor,
  auth: false,
  operationalBlock: null,
};

// Function to restore twoFactor state from localStorage
const restoreTwoFactorState = () => {
  try {
    const savedState = getSessionItem(STORAGE_KEYS.TWO_FACTOR_STATE);
    if (savedState) return JSON.parse(savedState);
  } catch (e) {}
  return twoFactor;
};

// Function to save twoFactor state to localStorage
const saveTwoFactorState = (state) => {
  try {
    setSessionItem(STORAGE_KEYS.TWO_FACTOR_STATE, JSON.stringify(state));
  } catch (e) {}
};

// Function to clear twoFactor state from localStorage
const clearTwoFactorState = () => {
  try {
    removeSessionItem(STORAGE_KEYS.TWO_FACTOR_STATE);
  } catch (e) {}
};

const clearSsoState = () => {
  clearSsoLoginFlags();
};

const readActiveSsoRedirectUrl = () => {
  try {
    return isActiveSsoHandoff() ? localStorage.getItem(STORAGE_KEYS.SSO_REDIRECT_URL) : null;
  } catch (e) {
    return null;
  }
};

const applyLocale = (payload) => {
  try {
    if (payload?.accessToken) {
      // Keep the locale chosen at login (selector / `lang` URL param) when the
      // user profile has no explicit language, so login stays in sync with the app.
      const locale = getDefaultLocale(payload, { fallbackToCurrent: true });
      setLocale(locale, false);
      moment.locale(locale);
    }
  } catch (e) {}
};

const notifySessionEnded = () => {
  try {
    const { formatMessage } = getIntl();
    notification.info({
      key: 'session-ended-elsewhere',
      message: formatMessage({ id: 'global.text.session.ended.title' }),
      description: formatMessage({ id: 'global.text.session.ended.description' }),
      duration: 8,
    });
  } catch (e) {}
};

// Client redirectUrl so the page can hand the code back to the client
const gotoSsoSuccess = (exchangeCode, redirectUrl) => {
  const params = new URLSearchParams({ code: exchangeCode });
  if (redirectUrl) params.set('redirectUrl', redirectUrl);
  history.replace(`/user/login/sso-redirect?${params.toString()}`);
};

/**
 * Handle 403 operational access block from login / 2FA / OAuth.
 * Returns payload for setOperationalBlock, or null if not an operational block.
 */
async function handleLoginOperationalBlock(error) {
  const {
    hasRestrictedBillingToken,
    isOperationalAccessBlockedResponse,
  } = require('@/utils/infrastructure/auth');
  const {
    handleOperationalAccessBlock,
  } = require('@/utils/infrastructure/communication/authUtils');

  if (!isOperationalAccessBlockedResponse(error)) return null;

  await handleOperationalAccessBlock(error, { fromRefresh: false, source: 'login' });
  return {
    authResult: error.authResult,
    code: error.code,
    message: error.message,
    status: error.status,
    hasRestrictedToken: hasRestrictedBillingToken(error),
  };
}

const Model = {
  namespace: 'auth',
  state: {
    ...initialState,
    twoFactor: restoreTwoFactorState(),
  },
  effects: {
    *login({ payload }, { call, put, select }) {
      try {
        const token = yield call(authLogin, payload);

        // Check if this is an SSO response (status 206) with exchangeCode
        if (
          (token.status === 206 || token.code === '206' || token.status === 2) &&
          token.ssoRequired &&
          token.exchangeCode
        ) {
          const redirectUrl = payload.redirectUrl || readActiveSsoRedirectUrl();
          clearSsoState();
          clearTwoFactorState();
          // SSO success page shows, then hands the exchange code back to the client.
          gotoSsoSuccess(token.exchangeCode, redirectUrl);
          return { success: true };
        }

        if (payload.accessToken) {
          yield put({
            type: 'userContext/defaultContext',
            payload: jwtDecode(payload.accessToken),
          });
        }

        const appLoginAppId = resolveAppLoginId(payload);
        yield put({
          type: 'changeLoginStatus',
          payload: {
            ...token,
            ...(_.has(token, 'twoFactorRequired') && { ...payload }),
            ...(appLoginAppId && { _appLoginAppId: appLoginAppId }),
            ...withPortalLoginPayload(payload),
          },
        });

        yield put({ type: 'updateAuthorityAfterLogin', payload: token });
        applyLocale(token);

        // SSO-required login must never land on the app home; hold on a blank page.
        if (payload.isSsoFlow) {
          history.replace('/user/login/sso-redirect');
          return { success: true };
        }

        if (!payload.isAutoSignIn) {
          const portalId = readPortalLoginId(payload);
          if (portalId && (yield enterPortalAfterLogin(portalId, { select, call, put }))) {
            return { success: true };
          }
          clearPortalLoginHandoff();
          authRedirect(token);
        }

        return { success: true };
      } catch (e) {
        const blockPayload = yield call(handleLoginOperationalBlock, e);
        if (blockPayload) {
          yield put({ type: 'setOperationalBlock', payload: blockPayload });
          return { success: false, operationalBlock: true, error: e };
        }

        // Stay on the current login URL so ?redirect= / portal / SSO params
        // survive a failed password attempt. Portal auto sign-in handles its
        // own failure (retry / portal login) — bouncing here drops Automy.
        if (!payload.isAutoSignIn && !window.location.pathname.startsWith('/user/login')) {
          const portal = yield select((o) => o.companyPortal.portal);
          history.replace(
            buildLoginUrl({
              portalId: isActivePortalContext() ? portal?.id : undefined,
            }),
            history.location?.state,
          );
        }

        // Return a result indicating failure instead of throwing
        return { success: false, error: e };
      }
    },

    /**
     * APP → AUTOMY, the inverse of appAuth's `/auth/automy` + appId header.
     * The app-scoped token is sent as the password; no appId header so the
     * backend issues a regular Automy token.
     */
    *exchangeAppToAutomy({ payload }, { call, put }) {
      try {
        if (getCurrentSession()?.type === SESSION_TYPES.PORTAL) return false;

        const ownerId = payload?.userId || getCurrentSession()?.userId;
        if (!ownerId) return false;

        const cred = payload?.token ? payload : findExchangeableAppCredential(ownerId);
        if (!cred?.userId || String(cred.userId) !== String(ownerId)) return false;

        const accessToken = cred?.token?.accessToken || cred?.token;
        if (!accessToken) return false;

        const res = yield call(authLogin, {
          username: 'automy',
          route: '/auth/automy',
          password: accessToken,
          suppressErrorNotification: true,
        });
        if (!res?.accessToken) return false;

        // Still inside `/apps/:id`: persist AUTOMY beside the APP session so the
        // shell can pick it up later. Switching the index now would stop APP refresh.
        if (getAppIdFromPath()) {
          const user = jwtDecode(res.accessToken);
          const userId = user.primarysid;
          if (userId) {
            const tokenData = JSON.stringify({ ...res, issuedAt: Date.now() });
            setItem(SESSION_TYPES.AUTOMY, userId, null, STORAGE_KEYS.AUTH_TOKEN, tokenData);
            if (user.timeZone) {
              setItem(
                SESSION_TYPES.AUTOMY,
                userId,
                null,
                STORAGE_KEYS.USER_TIMEZONE,
                user.timeZone,
              );
            }
            // Roles must live in the AUTOMY namespace too. Otherwise `/` mounts
            // with a token but getAuthority() is empty and the sider stays blank.
            try {
              const roles = Array.isArray(user.roles) ? user.roles : JSON.parse(user.roles);
              if (roles) {
                setItem(
                  SESSION_TYPES.AUTOMY,
                  userId,
                  null,
                  STORAGE_KEYS.AUTHORITY,
                  JSON.stringify(roles),
                );
              }
            } catch (e) {}
          }
          return true;
        }

        yield put({
          type: 'changeLoginStatus',
          payload: { ...res, _skipAppLogin: true },
        });
        yield put({ type: 'updateAuthorityAfterLogin', payload: res });
        applyLocale(res);

        try {
          yield put({
            type: 'userContext/defaultContext',
            payload: jwtDecode(res.accessToken),
          });
        } catch (e) {}

        return true;
      } catch (e) {
        return false;
      }
    },

    *validateSsoClient({ payload }, { call, put, select }) {
      try {
        yield call(validateSsoClient, payload);
        return { success: true };
      } catch (e) {
        return { success: false, error: e };
      }
    },

    *oauth2Login({ payload }, { call, put, select }) {
      try {
        const res = yield call(authPartyLogin, payload);
        const appLoginAppId = resolveAppLoginId(payload);

        if (res.status === 206 || res.code === '206' || res.status === 2) {
          if (res.ssoRequired && res.exchangeCode) {
            const redirectUrl = readActiveSsoRedirectUrl();
            clearSsoState();
            clearTwoFactorState();
            // SSO success page shows, then hands the exchange code back to the client.
            gotoSsoSuccess(res.exchangeCode, redirectUrl);
            return;
          }
        }

        if (appLoginAppId && !res.accessToken) {
          setGlobalItem(STORAGE_KEYS.APP_LOGIN_CONTEXT, appLoginAppId);
        }

        yield put({
          type: 'changeLoginStatus',
          payload: {
            ...res,
            ...(appLoginAppId && { _appLoginAppId: appLoginAppId }),
            ...withPortalLoginPayload(payload),
          },
        });

        yield put({ type: 'updateAuthorityAfterLogin', payload: res });
        applyLocale(res);

        if (res.twoFactorRequired && !res.accessToken) {
          const portalState = yield select((state) => state.companyPortal.portal);
          history.replace(
            buildLoginUrl({
              pathname: '/user/login/two-factor',
              portalId: isActivePortalContext() ? portalState?.id : undefined,
              appId: appLoginAppId,
              redirect: getGlobalItem(STORAGE_KEYS.APP_REDIRECT_AFTER_LOGIN) || undefined,
            }),
          );
          return;
        }

        const ssoRedirectUrl = readActiveSsoRedirectUrl();

        if (ssoRedirectUrl) {
          const ssoClientId = localStorage.getItem('sso-client-id');
          const ssoProvider = localStorage.getItem('sso-provider');

          const portalState = yield select((state) => state.companyPortal.portal);

          clearSsoState();
          clearTwoFactorState();

          try {
            const url = new URL(ssoRedirectUrl, window.location.origin);

            if (isActivePortalContext() && portalState?.id) {
              url.searchParams.set('portal', portalState.id);
            }
            if (ssoClientId && !url.searchParams.get('clientId')) {
              url.searchParams.set('clientId', ssoClientId);
            }

            if (ssoProvider && !url.searchParams.get('provider')) {
              url.searchParams.set('provider', ssoProvider);
            }

            window.location.href = url.toString();
            return;
          } catch (e) {}
        }

        // SSO-required login must never land on the app home; hold on a blank page.
        if (payload.isSsoFlow) {
          history.replace('/user/login/sso-redirect');
          return;
        }

        // OAuth lands on `/` inside BasicLayout. A same-origin history.replace
        // would skip handleFirstLoadData and leave the header without user/context.
        const oauthPortalId = readPortalLoginId(payload);
        if (oauthPortalId && (yield enterPortalAfterLogin(oauthPortalId, { select, call, put }))) {
          return;
        }
        clearPortalLoginHandoff();
        applyPostLoginRedirect(consumePostLoginRedirect(), { forceReload: true });
      } catch (e) {
        const blockPayload = yield call(handleLoginOperationalBlock, e);
        if (blockPayload) {
          yield put({ type: 'setOperationalBlock', payload: blockPayload });
          return;
        }

        const ssoRedirectUrl = readActiveSsoRedirectUrl();
        const ssoClientId = ssoRedirectUrl ? localStorage.getItem('sso-client-id') : null;
        const ssoProvider = ssoRedirectUrl ? localStorage.getItem('sso-provider') : null;

        localStorage.removeItem('sso-automatic-flow');
        localStorage.removeItem('sso-provider');
        localStorage.setItem('sso-auto-disabled', 'true');

        const portal = yield select((state) => state.companyPortal.portal);
        history.replace(
          buildLoginUrl({
            portalId: isActivePortalContext() ? portal?.id : undefined,
            appId: resolveAppLoginId(payload),
            extra: {
              ...(ssoClientId && { clientId: ssoClientId }),
              ...(ssoRedirectUrl && { redirectUrl: ssoRedirectUrl }),
              ...(ssoProvider && { provider: ssoProvider }),
            },
          }),
          history.location?.state,
        );
      }
    },

    *sendTwoFactorCode({ payload }, { call, put, select }) {
      try {
        const data = yield select((st) => st.auth.twoFactor);
        // Only send the necessary data, avoiding circular references
        const requestData = {
          twoFactorProvider: payload.twoFactorProvider?.value || payload.twoFactorProvider,
          username: data.username,
          password: data.password,
        };

        yield call(sendTwoFactorAuthCode, requestData);
        const newTwoFactorState = { ...data, ...payload, codeSent: true };
        saveTwoFactorState(newTwoFactorState);

        yield put({ type: 'queryTwoFactorCode', payload: newTwoFactorState });
      } catch (e) {}
    },

    *validateCode({ payload }, { call, put, select }) {
      try {
        const data = yield select((st) => st.auth.twoFactor);
        const ssoClientId = localStorage.getItem('sso-client-id');
        const isSsoFlow = !!ssoClientId && isActiveSsoHandoff();

        const res = yield call(validateSecurityCode, {
          ...payload,
          ...data,
          ...(isSsoFlow && {
            isSsoFlow: true,
            ssoClientId,
          }),
        });

        // Check if this is an SSO response (status 206) with exchangeCode
        if (
          (res.status === 206 || res.code === '206' || res.status === 2) &&
          res.ssoRequired &&
          res.exchangeCode
        ) {
          const redirectUrl = readActiveSsoRedirectUrl();
          clearSsoState();
          clearTwoFactorState();
          // SSO success page shows, then hands the exchange code back to the client.
          gotoSsoSuccess(res.exchangeCode, redirectUrl);
          return;
        }

        const twoFactorAppId = resolveAppLoginId(res);
        let twoFactorPortalId = null;
        try {
          twoFactorPortalId = new URLSearchParams(window.location.search || '').get('portal');
        } catch (e) {}
        if (!twoFactorPortalId && isActivePortalContext()) {
          const portalState = yield select((state) => state.companyPortal.portal);
          twoFactorPortalId = portalState?.id || null;
        }
        yield put({
          type: 'changeLoginStatus',
          payload: {
            ...res,
            ...(twoFactorAppId && { _appLoginAppId: twoFactorAppId }),
            ...(twoFactorPortalId &&
              !twoFactorAppId && { isPortal: true, portalId: twoFactorPortalId }),
          },
        });
        yield put({ type: 'updateAuthorityAfterLogin', payload: res });
        applyLocale(res);

        if (res.accessToken) {
          yield put({
            type: 'userContext/defaultContext',
            payload: jwtDecode(res.accessToken),
          });
        }

        const ssoRedirectUrl = readActiveSsoRedirectUrl();

        if (ssoRedirectUrl) {
          const ssoClientId = localStorage.getItem('sso-client-id');
          const ssoProvider = localStorage.getItem('sso-provider');
          const portalState = yield select((state) => state.companyPortal.portal);

          clearSsoState();
          clearTwoFactorState();

          try {
            const url = new URL(ssoRedirectUrl, window.location.origin);
            if (isActivePortalContext() && portalState?.id) {
              url.searchParams.set('portal', portalState.id);
            }
            if (ssoClientId && !url.searchParams.get('clientId')) {
              url.searchParams.set('clientId', ssoClientId);
            }
            if (ssoProvider && !url.searchParams.get('provider')) {
              url.searchParams.set('provider', ssoProvider);
            }
            window.location.href = url.toString();
            return;
          } catch (e) {}
        }

        // SSO-required login must never land on the app home; hold on a blank page.
        if (isSsoFlow) {
          history.replace('/user/login/sso-redirect');
          return;
        }

        const portalId = twoFactorPortalId || readPortalLoginId(payload);
        if (portalId && (yield enterPortalAfterLogin(portalId, { select, call, put }))) {
          return;
        }
        clearPortalLoginHandoff();
        authRedirect(res);
      } catch (e) {
        const blockPayload = yield call(handleLoginOperationalBlock, e);
        if (blockPayload) {
          yield put({ type: 'setOperationalBlock', payload: blockPayload });
        }
      }
    },

    *refreshToken({}, { call, put }) {
      try {
        const session = resolveRefreshSession();
        const token = session?.userId
          ? getItem(session.type, session.userId, session.resourceId, STORAGE_KEYS.AUTH_TOKEN)
          : getSessionItem(STORAGE_KEYS.AUTH_TOKEN);
        const res = yield call(authRefreshToken, token, buildRefreshContext(session));

        const active = getCurrentSession();
        const writesToActive =
          !session?.userId ||
          (active &&
            active.type === session.type &&
            String(active.userId) === String(session.userId) &&
            String(active.resourceId || '') === String(session.resourceId || ''));

        if (res.accessToken && writesToActive) {
          yield put({
            type: 'userContext/defaultContext',
            payload: jwtDecode(res.accessToken),
          });
        }

        // Write back into the session we refreshed. _skipAppLogin alone would
        // send an APP refresh through resolvePostLoginSession and stamp the
        // APP JWT into AUTOMY.
        yield put({
          type: 'changeLoginStatus',
          payload: { ...res, ...buildRefreshLoginPayload(session) },
        });
        if (writesToActive) {
          yield put({ type: 'updateAuthorityAfterLogin', payload: res });
        }
        applyLocale(res);
        yield put({ type: 'user/fetchCurrent', payload: true });
      } catch (e) {
        const { isOperationalRefreshBlockedResponse } = require('@/utils/infrastructure/auth');
        const {
          handleOperationalRefreshBlock,
        } = require('@/utils/infrastructure/communication/authUtils');

        if (isOperationalRefreshBlockedResponse(e)) {
          // Prefer the shared handler so tokens/queue/navigation stay consistent
          // with the interceptor path. Avoid generic logout ("sesión expirada").
          yield call(handleOperationalRefreshBlock, e);
          yield put({
            type: 'setOperationalBlock',
            payload: {
              authResult: e.authResult,
              code: e.code,
              message: e.message,
              status: e.status,
            },
          });
          return;
        }

        // A failed refresh means the session can no longer be recovered. Swallowing
        // the error here used to leave the user on a dead session; force a logout so
        // they are redirected to login instead.
        yield put({ type: 'logout' });
      }
    },

    *updateAuthorityAfterLogin({ payload }, {}) {
      try {
        if (payload.accessToken) {
          const user = jwtDecode(payload.accessToken);
          const roles = Array.isArray(user.roles) ? user.roles : JSON.parse(user.roles);
          setAuthority(roles);
        }
      } catch (e) {}
    },

    *cleanupTwoFactor(o, { put }) {
      clearTwoFactorState();
      yield put({ type: 'cleanTwoFactor' });
    },

    *cleanConfirmAccount(o, { put }) {
      yield put({ type: 'cleanAccountConfirm' });
    },

    *confirmAccount({ payload }, { call, put, select }) {
      try {
        const res = yield call(accountConfirm, payload);
        if (res?.message) {
          notification.success({
            message: getIntl().formatMessage({ id: 'user.twoFactor.alert.title' }),
            description: res.message,
          });
        }
        yield put({ type: 'queryConfirm', payload: true });
      } catch (e) {
        const portal = yield select((state) => state.companyPortal.portal);
        const keepPortal = isActivePortalContext();
        history.replace(
          {
            pathname: '/user/login',
            ...(keepPortal &&
              portal?.id && {
                query: { portal: portal.id },
              }),
          },
          history.location?.state || (keepPortal ? portal : undefined),
        );
      }
    },

    *logout(o, { call, put, select }) {
      if (window.location.pathname !== '/user/login') {
        sessionStorage.setItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS, 'true');
        try {
          // Before authLogout: the device DELETE still needs a valid token.
          // Bounded and never rejects, so it cannot hold up or break the logout.
          const { disablePushWithin } = require('@/utils/infrastructure/pwa/push');
          yield call(disablePushWithin, 1500);
          yield call(authLogout);
          yield put({ type: 'cleanup' });
          yield put({ type: 'user/clearCurrentUser' });
          yield put({ type: 'sessionNavigation/reset' });

          // Read portal from DVA state BEFORE clearing session
          const portalState = yield select((state) => state.companyPortal.portal);
          const currentSession = getCurrentSession();
          const appIdFromPath = getAppIdFromPath();
          const userId = currentSession?.userId || findAppSessionUserId(appIdFromPath);

          // Clear ALL sessions for this user (AUTOMY + PORTAL + APP)
          if (userId) {
            clearAllUserSessions(userId);
          } else {
            removeSessionItem(STORAGE_KEYS.AUTH_TOKEN);
            removeSessionItem(STORAGE_KEYS.AUTH_USER);
            removeSessionItem(STORAGE_KEYS.USER_CONTEXT);
            removeSessionItem(STORAGE_KEYS.AUTHORITY);
            removeSessionItem(STORAGE_KEYS.USER_TIMEZONE);
          }

          if (appIdFromPath) dropAppSessionOnLogout(appIdFromPath);
          else clearAppLoginFlags();
          clearSsoLoginFlags();

          clearTwoFactorState();
          setCurrentSession(null);

          const keepPortal = isActivePortalContext(currentSession);
          if (keepPortal && portalState) {
            stashPortalLoginConfig(portalState);
            applyPortalShellBrand(portalState);
          } else {
            // The outgoing tenant's brand must not survive into Automy login.
            resetWhiteLabelToDefault();
          }

          history.replace(
            keepPortal
              ? buildPortalLoginUrl(portalState, {
                  appId: appIdFromPath,
                  redirect: appIdFromPath
                    ? `${window.location.origin}/apps/${appIdFromPath}`
                    : undefined,
                })
              : buildLoginUrl({
                  appId: appIdFromPath,
                  redirect: appIdFromPath
                    ? `${window.location.origin}/apps/${appIdFromPath}`
                    : undefined,
                }),
            keepPortal ? portalState : history.location.state,
          );
        } finally {
          sessionStorage.removeItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS);
        }
      }
    },

    /**
     * Follow a session that was ended somewhere else out of this tab.
     *
     * Credentials are shared through localStorage, so a logout in any tab has
     * already unauthenticated this one — it just keeps rendering the previous
     * user's data, with its socket still joined, until some call returns a 401.
     *
     * The session is already revoked server-side, so this must not call the
     * logout endpoint again: it only drops what this tab holds.
     */
    *sessionEndedElsewhere(o, { put, select }) {
      if (window.location.pathname.startsWith('/user/login')) return;

      const { cleanupLoginData } = require('@/utils/infrastructure/communication/authUtils');
      const { isPublicFragment } = require('@/utils/infrastructure/communication/tokenManager');

      // Anonymous surfaces (public/external forms, account confirmation) run on
      // their own request token: they neither read nor need the user session, so
      // ending it must not eject whoever is filling one in.
      if (isPublicFragment()) return;

      // Screens watch this flag to stop refetching while a session is torn down.
      sessionStorage.setItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS, 'true');

      // Read the portal before the cleanup: its config lived in the namespace the
      // other tab already wiped, so storage can no longer tell us we are on one.
      const portal = yield select((state) => state.companyPortal?.portal);
      const keepPortal = isActivePortalContext();

      // Joined rooms and registered listeners belong to the ended session; the
      // layout re-initialises the socket when a new one mounts it.
      try {
        SocketService.getInstance().disconnect();
      } catch (e) {}

      // Drops the leftovers of a storage cleanup done by another tab, sends this
      // one to login and — through `sessionStorage.clear()` — releases the flag
      // set above along with the session index that got us here.
      cleanupLoginData(true, { portalId: keepPortal ? portal?.id : undefined });
      if (keepPortal && portal) {
        stashPortalLoginConfig(portal);
        applyPortalShellBrand(portal);
        yield put({ type: 'companyPortal/config', payload: portal });
      } else {
        resetWhiteLabelToDefault();
      }

      yield put({ type: 'cleanup' });
      yield put({ type: 'user/clearCurrentUser' });
      yield put({ type: 'sessionNavigation/reset' });
      yield put({ type: 'userContext/cleanup' });
      yield put({ type: 'chat/resetChatState' });

      notifySessionEnded();
    },

    *cleanup(o, { put }) {
      yield put({ type: 'reset' });
    },
  },
  reducers: {
    changeLoginStatus(state, { payload }) {
      try {
        if (payload.accessToken) {
          // Successful normal token must clear any prior operational block, otherwise
          // SecurityLayout keeps redirecting to /user/login/access-blocked.
          try {
            const {
              clearOperationalAuthBlock,
              clearRestrictedBillingSession,
            } = require('@/utils/infrastructure/auth');
            clearOperationalAuthBlock();
            clearRestrictedBillingSession();
          } catch (e) {}

          const user = jwtDecode(payload.accessToken);
          const userId = user.primarysid;

          const appLoginId = resolveAppLoginId(payload);

          const { _appLoginAppId, _skipAppLogin, ...tokenPayload } = payload;
          const tokenData = JSON.stringify({ ...tokenPayload, issuedAt: Date.now() });

          if (appLoginId && userId) {
            setItem(
              SESSION_TYPES.APP,
              userId,
              String(appLoginId),
              STORAGE_KEYS.AUTH_TOKEN,
              tokenData,
            );
            setItem(
              SESSION_TYPES.APP,
              userId,
              String(appLoginId),
              STORAGE_KEYS.USER_TIMEZONE,
              user.timeZone,
            );
            // Keep the handoff flag so getSessionToken() can read this token
            // while the tab is still on /user/login or the OAuth callback.
            setGlobalItem(STORAGE_KEYS.APP_LOGIN_CONTEXT, String(appLoginId));
            setGlobalItem(STORAGE_KEYS.APP_LOGIN_HANDOFF, String(appLoginId));
            setCurrentSession({
              type: SESSION_TYPES.APP,
              userId,
              resourceId: String(appLoginId),
            });
          } else {
            removeGlobalItem(STORAGE_KEYS.APP_LOGIN_CONTEXT);
            const session = resolvePostLoginSession({
              userId,
              isPortal: !!payload.isPortal || !!payload.headers?.portalId,
              portalId: payload.portalId || payload.headers?.portalId || null,
            });
            const storedSession = getCurrentSession();
            const sessionChanged =
              !storedSession ||
              storedSession.type !== session.type ||
              String(storedSession.userId) !== String(session.userId) ||
              String(storedSession.resourceId || '') !== String(session.resourceId || '');

            if (sessionChanged) {
              setCurrentSession(session);
            }
            if (session.type === SESSION_TYPES.PORTAL) {
              try {
                const pendingConfig = sessionStorage.getItem(STORAGE_KEYS.PORTAL_CONFIG);
                if (pendingConfig) {
                  setItem(
                    SESSION_TYPES.PORTAL,
                    session.userId,
                    session.resourceId,
                    STORAGE_KEYS.PORTAL_CONFIG,
                    pendingConfig,
                  );
                  sessionStorage.removeItem(STORAGE_KEYS.PORTAL_CONFIG);
                }
              } catch (e) {}
            }

            setItem(
              session.type,
              session.userId,
              session.resourceId,
              STORAGE_KEYS.AUTH_TOKEN,
              tokenData,
            );
            setItem(
              session.type,
              session.userId,
              session.resourceId,
              STORAGE_KEYS.USER_TIMEZONE,
              user.timeZone,
            );
          }

          moment.tz.setDefault(user.timeZone);
          setAuthority(Array.isArray(user.roles) ? user.roles : JSON.parse(user.roles));
        }
      } catch (e) {}

      const newState = {
        ...state,
        status: payload.status,
        auth: !!payload.accessToken,
        type: payload.type,
        ...(payload.accessToken && { operationalBlock: null }),
        ...(payload.twoFactorRequired && {
          twoFactor: {
            ...state.twoFactor,
            required: payload.twoFactorRequired,
            providers: _.map(payload.twoFactorProviders, (str) => ({
              label: `user.twoFactor.provider.${str}`,
              value: str,
            })),
            ..._.pick(payload, ['password', 'username']),
          },
        }),
        ...(payload.accessToken && { twoFactor }),
      };

      // Save twoFactor state if required
      if (payload.twoFactorRequired) saveTwoFactorState(newState.twoFactor);
      else if (payload.accessToken) clearTwoFactorState();
      return newState;
    },

    queryTwoFactorCode(state, { payload }) {
      const newTwoFactorState = { ...state.twoFactor, ...payload };
      saveTwoFactorState(newTwoFactorState);
      return {
        ...state,
        twoFactor: newTwoFactorState,
      };
    },

    queryConfirm(state, { payload }) {
      return {
        ...state,
        accountConfirmed: payload,
      };
    },

    cleanTwoFactor(state) {
      clearTwoFactorState();
      return {
        ...state,
        twoFactor,
      };
    },

    cleanAccountConfirm(state) {
      return {
        ...state,
        accountConfirmed: false,
      };
    },

    setOperationalBlock(state, { payload }) {
      return {
        ...state,
        auth: false,
        operationalBlock: payload || null,
      };
    },

    clearOperationalBlock(state) {
      try {
        const {
          clearOperationalAuthBlock,
          clearRestrictedBillingSession,
        } = require('@/utils/infrastructure/auth');
        clearOperationalAuthBlock();
        clearRestrictedBillingSession();
      } catch (e) {}
      return {
        ...state,
        operationalBlock: null,
      };
    },

    reset() {
      clearTwoFactorState();
      setCurrentSession(null);
      return initialState;
    },
  },
  subscriptions: {
    sessionEnd({ dispatch }) {
      return subscribeToSessionEnd(() => {
        dispatch({ type: 'sessionEndedElsewhere' });
      });
    },
  },
};

export default Model;
